import React, { useEffect, useRef } from "react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useBookContext } from "../../../context/BookContext";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useT } from "@transifex/react";
import { getCurrentDateTimeFormatted } from "../../../helpers/DateTime";
import queryString from "query-string";
import ContentWithHeader from "../../../wrapper/ContentWithHeader/ContentWithHeader";
import { redirectScreenDelayMs } from "../../../constants/RedirectConstants";
import { trackTouchpointPageView } from "../../../networking/touchpoint";
import { hasStoredBookDeepLink, parseBookDeepLinkParams } from "../../../helpers/DeepLinkParams";

const CreateHome: React.FC = () => {
    const { setTestName, initAppWithBookAndUnit, unitsToPreloadIds, setUnitsToPreloadIds } = useTestCreationContext();

    const { checkForInitArticleId } = useBookContext();

    const { userId, setIsIframeMode, setCreatingMode } = useGeneralContext();

    const currentDate = getCurrentDateTimeFormatted();
    const { search } = useLocation();
    const urlParams = queryString.parse(search);
    const history = useHistory();

    const redirectTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        trackTouchpointPageView("create");
        return () => clearTimeout(redirectTimer.current);
    }, []);

    const hasDeepLinkParams = useRef(
        !!localStorage.getItem("initAID") ||
            hasStoredBookDeepLink() ||
            !!urlParams.articleId ||
            !!parseBookDeepLinkParams(urlParams) ||
            sessionStorage.getItem("deepLinkInProgress") === "true"
    );

    const t = useT();
    const t_redirecting = t("You are being redirected…", { _tags: "TeacherArea" });
    const t_giveADescriptionPlaceholder = t("Test from {currentDate}", {
        currentDate,
        _tags: "CreateTest,CreateHome",
    });

    // useEffect #1: Cornelsen deep-link handler — checks localStorage for initAID/initUAID
    useEffect(() => {
        if (unitsToPreloadIds.length > 0) {
            setUnitsToPreloadIds([]);
        }
        if (localStorage.getItem("initAID")) {
            sessionStorage.setItem("deepLinkInProgress", "true");
            checkForInitArticleId(t_giveADescriptionPlaceholder);
        } else if (hasStoredBookDeepLink()) {
            sessionStorage.setItem("deepLinkInProgress", "true");
            initAppWithBookAndUnit()
                .then((redirectTo) => {
                    clearTimeout(redirectTimer.current);
                    setCreatingMode(CreationModes.TEST);
                    setTestName(t_giveADescriptionPlaceholder);
                    history.push(redirectTo === "BOOK" ? "/create/select-book" : "/create/select-vocabulary");
                })
                .catch(() => {
                    sessionStorage.removeItem("deepLinkInProgress");
                    redirectTimer.current = setTimeout(() => history.push("/assistent"), redirectScreenDelayMs);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // useEffect #2: Redirect to assistant (anonymous test check is handled in AssistantOverview)
    useEffect(() => {
        if (hasDeepLinkParams.current || sessionStorage.getItem("deepLinkInProgress") === "true") return;

        if (userId) {
            redirectTimer.current = setTimeout(() => history.push("/assistent"), redirectScreenDelayMs);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // useEffect #4: URL params processing (articleId, articleUuid, iframeMode)
    useEffect(() => {
        if (urlParams.iframeMode) {
            setIsIframeMode(true);
        }
        const bookDeepLink = parseBookDeepLinkParams(urlParams);
        if (urlParams.articleId) {
            sessionStorage.setItem("deepLinkInProgress", "true");
            const aId: string = urlParams.articleId as string;
            localStorage.setItem("initAID", aId);
            checkForInitArticleId(t_giveADescriptionPlaceholder);
        } else if (bookDeepLink) {
            sessionStorage.setItem("deepLinkInProgress", "true");
            initAppWithBookAndUnit(bookDeepLink.articleUuid, bookDeepLink.unitFromUuid, bookDeepLink.unitToUuid)
                .then((redirectTo) => {
                    clearTimeout(redirectTimer.current);
                    setCreatingMode(CreationModes.TEST);
                    setTestName(t_giveADescriptionPlaceholder);
                    history.push(redirectTo === "BOOK" ? "/create/select-book" : "/create/select-vocabulary");
                })
                .catch(() => {
                    sessionStorage.removeItem("deepLinkInProgress");
                    history.push("/create/select-book");
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    if (!userId) {
        return <Redirect to="/auth" />;
    }

    return (
        <ContentWithHeader isAreaHeader>
            <div className="flex flex-1 items-center justify-center">
                <p className="text-xl">{t_redirecting}</p>
            </div>
        </ContentWithHeader>
    );
};

export default CreateHome;
